// src/features/messages/components/UnreadMessagesDialog.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChevronLeft as PreviousIcon, DoneAll as DoneAllIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { messagesAPI } from '../services/messages';
import { useMessageThread } from '../hooks/useMessageThread';
import { useNotificationsContext } from '../../../context/NotificationsContext';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import MessageThread from './MessageThread';

// The API's page limit; more than this unread is an inbox nobody reads one by one
const MAX_SHOWN = 100;

/**
 * Shown once when a staff member logs in (each time `userId` changes) if they
 * have unread messages, newest first. "Next" dismisses a message by marking it
 * read; reacting or commenting does too. "Later" closes without reading, so
 * the messages come back at the next login.
 */
export default function UnreadMessagesDialog({ userId }) {
  const { t } = useTranslation(['messages', 'common']);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { refreshMessageCount } = useNotificationsContext();
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    setOpen(false);
    if (!userId) return undefined;
    let cancelled = false;
    messagesAPI.list({ unread_only: true, limit: MAX_SHOWN })
      .then(({ data }) => {
        if (cancelled || !data.items.length) return;
        setQueue(data.items);
        setIndex(0);
        setOpen(true);
      })
      // Not worth bothering anyone about; the badge still shows the count
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  const current = open ? queue[index] : null;
  const thread = useMessageThread(current?.message_id);
  const isLast = index >= queue.length - 1;

  const close = () => {
    setOpen(false);
    refreshMessageCount();
  };

  const handleNext = async () => {
    setWorking(true);
    const ok = await thread.markRead();
    setWorking(false);
    if (!ok) return;
    if (isLast) close();
    else setIndex(i => i + 1);
  };

  const readAll = async () => {
    setWorking(true);
    try {
      await messagesAPI.markAllRead();
      close();
    } catch (err) {
      showError(err?.response?.data?.message || t('messages:feedback.action_error'));
    } finally {
      setWorking(false);
    }
  };

  // Skips every remaining message at once, so it asks first
  const confirmReadAll = () => showConfirmDialog({
    title: t('messages:unread_dialog.read_all_title'),
    message: t('messages:unread_dialog.read_all_text', { count: queue.length - index }),
    confirmText: t('messages:unread_dialog.read_all'),
    severity: 'info',
    onConfirm: readAll,
  });

  if (!current) return null;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" fullScreen={fullScreen} scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>{t('messages:unread_dialog.title', { count: queue.length })}</Box>
        {queue.length > 1 && (
          <Chip size="small" label={t('messages:unread_dialog.position', { current: index + 1, total: queue.length })} />
        )}
      </DialogTitle>
      <DialogContent dividers>
        <MessageThread thread={thread} prominentReactions />
      </DialogContent>
      {/* Leaving (Later / mark all) on one side, reading on (Previous / Next) on the other.
          On a phone the reading buttons go on top at full width */}
      <DialogActions disableSpacing sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, order: { xs: 2, sm: 1 }, width: { xs: '100%', sm: 'auto' }, justifyContent: 'space-between' }}>
          <Button onClick={close} color="inherit" disabled={working}>
            {t('messages:unread_dialog.later')}
          </Button>
          {queue.length - index > 1 && (
            <Button onClick={confirmReadAll} color="inherit" startIcon={<DoneAllIcon />} disabled={working}>
              {t('messages:unread_dialog.read_all')}
            </Button>
          )}
        </Box>
        <Box sx={{ flex: 1, order: { xs: 0, sm: 2 }, display: { xs: 'none', sm: 'block' } }} />
        <Box sx={{ display: 'flex', gap: 1, order: { xs: 1, sm: 3 }, width: { xs: '100%', sm: 'auto' } }}>
          {index > 0 && (
            <Button onClick={() => setIndex(i => i - 1)} startIcon={<PreviousIcon />} disabled={working}>
              {t('messages:unread_dialog.previous')}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={working || thread.loading}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            {isLast ? t('messages:unread_dialog.ok') : t('messages:unread_dialog.next')}
          </Button>
        </Box>
      </DialogActions>
      <ConfirmDialog />
    </Dialog>
  );
}
